import { defaultCardStyle } from "components/ui/card";
import React from "react";
import { createPortal } from "react-dom";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { useNavbarPortal } from "./navbar-portal-context";

interface ContentProps {
    children?: React.ReactNode;
    className?: string;
}

/** `ContentContainer` + `ContentFull` combined. Use when you want a single bordered card with responsive page margins. */
export const Content: React.FC<ContentProps> = props => (
    <ContentContainer className={props.className}>
        <ContentFull>{props.children}</ContentFull>
    </ContentContainer>
);

interface ContentContainerProps {
    className?: string;
    children?: React.ReactNode;
}

/** Responsive horizontal margins/gaps; no card chrome. Use when the content brings its own bordered surface (or when you want to render onto the page background). */
export const ContentContainer: React.FC<ContentContainerProps> = props => (
    <div
        className={`mx-0 gap-1 md:mx-2 md:gap-2 lg:mx-4 lg:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

interface ContentFullProps {
    children?: React.ReactNode;
    className?: string;
}

/** Bordered card surface (white background + border + padding). Pair with `ContentContainer` for outer spacing, or use `Content` as a shorthand. */
export const ContentFull: React.FC<ContentFullProps> = props => {
    return (
        <div className={`w-full p-2 lg:p-4 ${defaultCardStyle} ${props.className ?? ""}`}>
            {props.children}
        </div>
    );
};

interface TilesProps {
    children?: React.ReactNode;
}

/** Responsive grid (1/2/3 columns by breakpoint) for `Tile` cards. */
export const Tiles: React.FC<TilesProps> = props => (
    <div className="grid grid-cols-1 w-full gap-0 md:gap-2 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
        {props.children}
    </div>
);

interface TileProps {
    children?: React.ReactNode;
}

/** Single bordered card cell for use inside `Tiles`. */
export const Tile: React.FC<TileProps> = props => (
    <div className="bg-white p-2 md:rounded-sm lg:p-4 lg:rounded rounded shadow">
        {props.children}
    </div>
);

interface StageProps {
    children?: React.ReactNode;
}

/** Semantic page-root wrapper (currently a passthrough fragment). Use as the outermost element of a page; pair with `PageHeader`. */
export const Stage: React.FC<StageProps> = props => {
    return <>{props.children}</>;
};

interface SectionHeaderProps {
    title: string;
    children?: React.ReactNode;
    stackBreakpoint?: "md" | "lg";
}

/** Page title header; portals into the mobile navbar on small screens. Use once per page as the top heading. */
export const PageHeader: React.FC<SectionHeaderProps> = props => {
    const responsiveMode = useResponsiveMode();
    const { portalTarget } = useNavbarPortal();
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;

    const flexClass =
        props.stackBreakpoint === "md"
            ? "px-4 py-2 lg:py-4 md:flex md:items-center md:justify-between"
            : props.stackBreakpoint === "lg"
              ? "px-4 py-2 lg:py-4 lg:flex lg:items-center lg:justify-between"
              : "px-4 py-2 lg:py-4 flex items-center justify-between";

    const content = (
        <div className={flexClass}>
            <h2 className="text-xl/8 text-zinc-950 sm:text-xl/8 twoverride">
                {props.title}
            </h2>
            <div>{props.children}</div>
        </div>
    );

    if (isMobile && portalTarget) {
        return createPortal(content, portalTarget);
    }

    return content;
};

/** Subsection heading within a page. No mobile-navbar portal behavior; use `PageHeader` for the top of the page. */
export const SectionHeader: React.FC<SectionHeaderProps> = props => {
    return (
        <div className="px-4 py-2 lg:py-4 lg:flex items-end justify-between">
            <h2 className="text-2xl/8 font-semibold text-zinc-950 sm:text-xl/8">
                {props.title}
            </h2>
            <div>{props.children}</div>
        </div>
    );
};

interface ContentHeaderProps {
    children?: React.ReactNode;
}

/** Plain `<h2 text-xl>` heading with no responsive or portal behavior. Legacy; prefer `PageHeader` for page titles. */
export const ContentHeader: React.FC<ContentHeaderProps> = props => (
    <h2 className="text-xl">{props.children}</h2>
);

interface ContentSubheaderProps {
    children?: React.ReactNode;
}

/** Small gray subtitle. Pairs with `ContentHeader`. */
export const ContentSubheader: React.FC<ContentSubheaderProps> = props => (
    <h6 className="text-gray-500 text-sm -mt-2 mb-4">{props.children}</h6>
);

interface ContentSection2UpProps {
    children?: React.ReactNode;
    className?: string;
}

/** Two-column flex layout at md+; stacks below md. Wrap each column in `ContentSectionColumn`. */
export const ContentSection2Up: React.FC<ContentSection2UpProps> = props => (
    <div
        className={`mx-0 max-md:space-y-4 md:flex md:mx-2 md:pb-2 md:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

/** Two-column flex layout at lg+; stacks below lg. Wrap each column in `ContentSectionColumn`. */
export const ContentSection2UpLg: React.FC<ContentSection2UpProps> = props => (
    <div
        className={`mx-0 gap-1 lg:flex lg:mx-2 lg:pb-2 lg:gap-4 ${
            props.className ?? ""
        }`}>
        {props.children}
    </div>
);

/** flex-1 column child for `ContentSection2Up` / `ContentSection2UpLg`. */
export const ContentSectionColumn: React.FC<
    React.HTMLAttributes<HTMLDivElement>
> = props => {
    const { className, ...rest } = props;
    const classNameActual = `${className ?? ""} flex-1`;
    return (
        <div {...rest} className={classNameActual}>
            {props.children}
        </div>
    );
};

interface EntityPageHeaderProps {
    children?: React.ReactNode;
}

/** Page header variant for entity show pages (Client / Community / Referral). Portals into mobile navbar on small screens. */
export const EntityPageHeader: React.FC<EntityPageHeaderProps> = props => {
    const responsiveMode = useResponsiveMode();
    const { portalTarget } = useNavbarPortal();
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;

    const content = (
        <div className="flex items-start flex-wrap justify-between sm:px-4 sm:pt-4 sm:pb-3">
            {props.children}
        </div>
    );

    if (isMobile && portalTarget) {
        return createPortal(content, portalTarget);
    }

    return content;
};
